import { DbRef } from "./firebase";
/** The SKU for a specimen bag */
export const SPECIMEN_BAG_SKU_ID = 'SPECIMEN_BAG_2';
/** The first part of a supply item ID for a specimen bag */
export const SPECIMEN_BAG_ID_PREFIX = `${SPECIMEN_BAG_SKU_ID}-`;
/**
 * The supply ID for SKU `SPECIMEN_BAG_2`. Currently the only supply ID we use
 * in the charting flow.
 */
export const SPECIMEN_BAG_SUPPLY_ID = 'xecya76wc5nl8y85x9k';
export var SupplyItemEventType;
(function (SupplyItemEventType) {
    SupplyItemEventType["CHARTING"] = "charting";
    SupplyItemEventType["LOCATION_UPDATE"] = "locationUpdate";
})(SupplyItemEventType || (SupplyItemEventType = {}));
export var ShopifyFulfillmentStatus;
(function (ShopifyFulfillmentStatus) {
    /** Shopify fulfillment order has been shipped through our shopify system */
    ShopifyFulfillmentStatus["SHIPPED"] = "shipped";
    /** Shopify fulfillment order has been successfully delivered through our shopify system */
    ShopifyFulfillmentStatus["DELIVERED"] = "delivered";
    /** Shopify fulfillment order has been successfully scanned and activated through the pro app by the nurse. */
    ShopifyFulfillmentStatus["ACTIVATED"] = "activated";
})(ShopifyFulfillmentStatus || (ShopifyFulfillmentStatus = {}));
export var RetireReason;
(function (RetireReason) {
    RetireReason["CONFIG_CHANGE"] = "Change in configuration";
    RetireReason["DEFECTIVE"] = "Defective batch";
    RetireReason["EXPIRED"] = "Expired";
})(RetireReason || (RetireReason = {}));
export var SupplyItemStatus;
(function (SupplyItemStatus) {
    /** SKU sheet with supply items have been entered */
    SupplyItemStatus["CREATED"] = "created";
    /**
     * Clinician has activated the supply item
     * indicating they have recieved the item
     */
    SupplyItemStatus["ACTIVATED"] = "activated";
    /** Supply item has been used in a procedure */
    SupplyItemStatus["USED"] = "used";
    /** Supply item has been dropped off at the lab */
    SupplyItemStatus["DROPPED_OFF"] = "droppedOff";
})(SupplyItemStatus || (SupplyItemStatus = {}));
export var SupplyItemType;
(function (SupplyItemType) {
    SupplyItemType["AMBER_BAG"] = "Amber Bag";
    SupplyItemType["CARESEND_BAG"] = "CareSend Bag";
    SupplyItemType["CONTAINER"] = "container";
    SupplyItemType["INSULATED_BAG"] = "Insulated Bag (Including ice pack)";
    SupplyItemType["MAIL_IN_BOX"] = "Mail-in box";
    SupplyItemType["PROCEDURE_BAG"] = "Procedure Bag";
    SupplyItemType["SPECIMEN_BAG"] = "Specimen Bag";
    SupplyItemType["TUBE"] = "tube";
})(SupplyItemType || (SupplyItemType = {}));
export var BarcodeType;
(function (BarcodeType) {
    /**
     * MicroPDF417 or Data Matrix barcode on a specific batch of supply items.
     * Typically a 10pk, with a format such as 10PKLAVTUBE0323-100000001-100000010
     */
    BarcodeType["BATCH_ITEM"] = "batchItem";
    /**
     * MicroPDF417, Data Matrix, or Code128 barcode. Can use with any value.
     */
    BarcodeType["GENERIC"] = "generic";
    /**
     * MicroPDF417 or Data Matrix barcode on a specific supply item. Typically
     * with a format such as LAVTUBE0323-100000001
     */
    BarcodeType["SUPPLY_ITEM"] = "supplyItem";
    /**
     * Code128 barcode from a shipping label. These codes are numeric only, and
     * include the tracking number in addition to other info. USPS example:
     * 420 (unknown) 90064 (zip code) 9300110944101908138404 (tracking number)
     * Full barcode: 420900649300110944101908138404
     */
    BarcodeType["SHIPPING_LABEL"] = "shippingLabel";
})(BarcodeType || (BarcodeType = {}));
export var SupplyInstructionOptions;
(function (SupplyInstructionOptions) {
    SupplyInstructionOptions["CENTRIFUGE"] = "centrifuge";
    SupplyInstructionOptions["LIGHT_SENSITIVE"] = "lightSensitive";
    SupplyInstructionOptions["REFRIGERATE_AFTER_PROCESSING"] = "refrigerateAfterProcessing";
    SupplyInstructionOptions["REFRIGERATED"] = "refrigerated";
    SupplyInstructionOptions["FILL_BY_PATIENT"] = "fillByPatient";
    SupplyInstructionOptions["TRANSFER_FROM_CONTAINER"] = "transferFromContainer";
    SupplyInstructionOptions["DISCARD_AFTER_TRANSFER"] = "discardAfterTransfer";
})(SupplyInstructionOptions || (SupplyInstructionOptions = {}));
export const LabelSupplyInstructionOption = {
    [SupplyInstructionOptions.LIGHT_SENSITIVE]: 'Protect from light',
    /**
     * Requires long-term refrigeration after processing. (i.e. after centrifuge
     * if applicable) This is different from the `refrigerated`  property on each
     * supply, which refers to putting in an insulated bag with ice pack
     * immediately after drawing.
     */
    [SupplyInstructionOptions.REFRIGERATE_AFTER_PROCESSING]: 'Refrigerate after processing',
    [SupplyInstructionOptions.REFRIGERATED]: 'Place on ice',
    [SupplyInstructionOptions.CENTRIFUGE]: 'Place in centrifuge',
    [SupplyInstructionOptions.FILL_BY_PATIENT]: 'Fill by patient',
    [SupplyInstructionOptions.TRANSFER_FROM_CONTAINER]: 'Transfer from container',
    [SupplyInstructionOptions.DISCARD_AFTER_TRANSFER]: 'Discard after tranfer',
};
export const initialPCRSupplyProcessing = {
    [SupplyInstructionOptions.REFRIGERATED]: false,
};
export const initialTubeSupplyProcessing = {
    [SupplyInstructionOptions.CENTRIFUGE]: false,
    [SupplyInstructionOptions.LIGHT_SENSITIVE]: false,
    [SupplyInstructionOptions.REFRIGERATED]: false,
};
export const initialUrineSupplyProcessing = {
    [SupplyInstructionOptions.DISCARD_AFTER_TRANSFER]: false,
    [SupplyInstructionOptions.FILL_BY_PATIENT]: false,
    [SupplyInstructionOptions.REFRIGERATED]: false,
    [SupplyInstructionOptions.TRANSFER_FROM_CONTAINER]: false,
};
export var SupplyType;
(function (SupplyType) {
    SupplyType["GENERIC"] = "generic";
    SupplyType["LAB_SHIPMENT_BOX"] = "labShipmentBox";
    SupplyType["PCR"] = "pcr";
    /**
     * Required to be scanned during packing and charting with or without
     * `disableSampleScanning` (Note that visit bags are not scanned if
     * sample scanning is disabled)
     */
    SupplyType["PROCEDURE_BAG"] = "procedureBag";
    /**
     * Required to be scanned during packing and charting with or without
     * `disableSampleScanning` (i.e., specimen bag, amber bag, insulated bag)
     */
    SupplyType["SAMPLE_BAG"] = "sampleBag";
    SupplyType["SHIPMENT_BOX"] = "shipmentBox";
    SupplyType["TUBE"] = "tube";
    SupplyType["URINE"] = "urine";
})(SupplyType || (SupplyType = {}));
export var SupplyTransferStatusName;
(function (SupplyTransferStatusName) {
    SupplyTransferStatusName["CANCELED"] = "canceled";
    /** Enqueued for additional information on dashboard */
    SupplyTransferStatusName["CREATED"] = "created";
    /** Location (placeID) is assigned to supply transfer */
    SupplyTransferStatusName["ASSIGNED"] = "assigned";
    /** When a packer starts packing the tranfer */
    SupplyTransferStatusName["IN_PROGRESS"] = "inProgress";
    /** Packing flow completed and shipping label affixed to shipment */
    SupplyTransferStatusName["PACKED"] = "packed";
})(SupplyTransferStatusName || (SupplyTransferStatusName = {}));
export var AlgoliaSupplyTransferTagName;
(function (AlgoliaSupplyTransferTagName) {
    AlgoliaSupplyTransferTagName["PACKER_IS_NOT_ASSIGNED"] = "packerIsNotAssigned";
})(AlgoliaSupplyTransferTagName || (AlgoliaSupplyTransferTagName = {}));
export const SupplyTransferRelationshipKeys = {
    draftWaypointID: { ref: DbRef.DRAFT_WAYPOINTS },
    packerID: { ref: DbRef.USERS },
    placeID: { ref: DbRef.PLACES },
    procedures: { ref: DbRef.PROCEDURES },
    shipmentIDs: { ref: DbRef.SHIPMENTS },
};
export var SupplyShipmentStatusName;
(function (SupplyShipmentStatusName) {
    /**
     * Draft supply shipment is a supply shipment that is not yet ready to be
     * shipped. It is a partial supply shipment with the addition of the
     * `shopifyInfo` field that is saved in the database.
     */
    SupplyShipmentStatusName["DRAFT"] = "draft";
    /** Shipping method is determined, no label attached */
    SupplyShipmentStatusName["PENDING_LABEL"] = "pendingLabel";
    /** Label is printed and attached to supply transfer */
    SupplyShipmentStatusName["READY_TO_SHIP"] = "readyToShip";
    /** Courier has picked up package and enroute to destination */
    SupplyShipmentStatusName["IN_TRANSIT"] = "inTransit";
    /** Package is delivered */
    SupplyShipmentStatusName["DELIVERED"] = "delivered";
})(SupplyShipmentStatusName || (SupplyShipmentStatusName = {}));
export var SupplyShipmentCarrier;
(function (SupplyShipmentCarrier) {
    SupplyShipmentCarrier["FED_EX"] = "fedEx";
    SupplyShipmentCarrier["UPS"] = "ups";
    SupplyShipmentCarrier["USPS"] = "usps";
})(SupplyShipmentCarrier || (SupplyShipmentCarrier = {}));
/** Ordered from shortest to longest in delivery time */
export var SupplyShipmentDeliveryType;
(function (SupplyShipmentDeliveryType) {
    SupplyShipmentDeliveryType["NEXT_DAY_AIR_EARLY"] = "next_day_air_early";
    SupplyShipmentDeliveryType["NEXT_DAY_AIR"] = "next_day_air";
    SupplyShipmentDeliveryType["NEXT_DAY_AIR_SAVER"] = "next_day_air_saver";
    SupplyShipmentDeliveryType["TWO_DAY_AIR"] = "two_day_air";
    SupplyShipmentDeliveryType["THREE_DAY_SELECT"] = "three_day_select";
    SupplyShipmentDeliveryType["GROUND"] = "ground";
})(SupplyShipmentDeliveryType || (SupplyShipmentDeliveryType = {}));
/**
 * Delivery methods that are currently available for selection when creating
 * a shipment. Previously active methods should not be removed from the enum
 * as they are still referenced in the database.
 */
export const activeDeliveryMethods = [
    SupplyShipmentCarrier.FED_EX,
    SupplyShipmentCarrier.UPS,
    SupplyShipmentCarrier.USPS,
];
export const ShipmentRelationshipKeys = {
    packerID: { ref: DbRef.USERS },
    placeID: { ref: DbRef.PLACES },
    procedures: { ref: DbRef.PROCEDURES },
    shipmentBoxSupplyID: { ref: DbRef.SUPPLIES },
    transferID: { ref: DbRef.SUPPLY_TRANSFERS },
    waypointActionID: { ref: DbRef.WAYPOINT_ACTIONS },
};
export var ShipmentType;
(function (ShipmentType) {
    /** Shipping samples to lab */
    ShipmentType["SAMPLE"] = "sample";
    /** Shipping supplies to patient */
    ShipmentType["SUPPLY"] = "supply";
})(ShipmentType || (ShipmentType = {}));
//# sourceMappingURL=supply.js.map